import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import Swal from "sweetalert2";
import { lojaService } from "../../../../services/lojaService";
import {
  metaColetivaService,
  MetaColetiva,
} from "../../../../services/metaColetivaService";
import { toast } from "react-toastify";
import { COLORS } from "../../../../constants";

export interface MetaColetivaFormData {
  inicializacaoNota: string;
  producaoNota: string;
  finalizacaoNota: string;
  tempoMeta: string;
  tempoReal: string;
  errosProducao: string;
  metaAtingida: string;
  observacao: string;
  lojaId: string;
}

const ITEMS_PER_PAGE = 10;

interface MetaColetivaContextType {
  filters: any;
  lojas: Array<{ id: number; nome: string }>;
  isOpen: boolean;
  editingItem: any;
  openModal: (item?: any) => void;
  closeModal: () => void;
  handleFieldChange: (name: string, value: any) => void;
  handleSearch: () => void;
  handleClear: () => void;
  metasColetivas: MetaColetiva[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  isLoading: boolean;
  handleSubmit: (data: any) => Promise<void>;
  handleEdit: (rowIndex: number) => void;
  handleDelete: (rowIndex: number) => void;
}

const MetaColetivaContext = createContext<MetaColetivaContextType | undefined>(
  undefined
);

export const useMetaColetiva = () => {
  const context = useContext(MetaColetivaContext);
  if (!context) {
    throw new Error(
      "useMetaColetiva must be used within a MetaColetivaProvider"
    );
  }
  return context;
};

export const MetaColetivaProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [lojas, setLojas] = useState<Array<{ id: number; nome: string }>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [metasColetivas, setMetasColetivas] = useState<MetaColetiva[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = ITEMS_PER_PAGE;

  const fetchLojas = useCallback(async () => {
    try {
      const data = await lojaService.getLojas();
      setLojas(data);
    } catch {
      toast.error("Erro ao carregar lojas");
    }
  }, []);

  useEffect(() => {
    fetchLojas();
  }, [fetchLojas]);

  const fetchMetasColetivas = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await metaColetivaService.getMetasColetivas({
        page: currentPage,
        limit: itemsPerPage,
        lojaId: appliedFilters.lojaId ?? undefined,
      });
      setMetasColetivas(res.data);
      setTotalItems(res.total);
      setTotalPages(res.totalPages);
    } catch {
      toast.error("Erro ao carregar metas coletivas");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedFilters.lojaId, itemsPerPage]);

  useEffect(() => {
    fetchMetasColetivas();
  }, [fetchMetasColetivas]);

  const openModal = (item?: any) => {
    setEditingItem(item || null);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setEditingItem(null);
  };

  const handleFieldChange = (name: string, value: any) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const handleClear = () => {
    setFilters({});
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const handleSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      if (editingItem) {
        await metaColetivaService.updateMetaColetiva(editingItem.id, data);
        toast.success("Meta coletiva atualizada com sucesso!");
      } else {
        await metaColetivaService.createMetaColetiva(data);
        toast.success("Meta coletiva criada com sucesso!");
      }
      closeModal();
      await fetchMetasColetivas();
    } catch {
      toast.error("Erro ao salvar meta coletiva");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (rowIndex: number) => {
    const item = metasColetivas[rowIndex];
    openModal(item);
  };

  const handleDelete = async (rowIndex: number) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não pode ser desfeita",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await metaColetivaService.deleteMetaColetiva(
          metasColetivas[rowIndex].id
        );
        Swal.fire({
          title: "Deletado!",
          text: "A meta coletiva foi removida com sucesso.",
          icon: "success",
          confirmButtonColor: COLORS.PRIMARY,
        });
        await fetchMetasColetivas();
      } catch {
        toast.error("Erro ao excluir meta coletiva");
      }
    }
  };

  const value = {
    filters,
    lojas,
    isOpen,
    editingItem,
    openModal,
    closeModal,
    handleFieldChange,
    handleSearch,
    handleClear,
    metasColetivas,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    setCurrentPage,
    isLoading,
    handleSubmit,
    handleEdit,
    handleDelete,
  };

  return (
    <MetaColetivaContext.Provider value={value}>
      {children}
    </MetaColetivaContext.Provider>
  );
};

export const timeToSeconds = (time: string): number => {
  if (!time || time.length < 8) return 0;
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const calculateMetaAtingida = (
  formData: MetaColetivaFormData
): string => {
  const inicializacao = parseFloat(formData.inicializacaoNota) || 0;
  const producao = parseFloat(formData.producaoNota) || 0;
  const finalizacao = parseFloat(formData.finalizacaoNota) || 0;

  const tempoMetaSeconds = timeToSeconds(formData.tempoMeta);
  const tempoRealSeconds = timeToSeconds(formData.tempoReal);

  if (
    !inicializacao ||
    !producao ||
    !finalizacao ||
    !tempoMetaSeconds ||
    !tempoRealSeconds
  ) {
    return "0.0";
  }

  const mediaNotas = (inicializacao + producao + finalizacao) / 3;

  const fatorTempo =
    tempoRealSeconds <= tempoMetaSeconds
      ? 1.0
      : tempoMetaSeconds / tempoRealSeconds;

  const metaAtingida = (mediaNotas / 10) * fatorTempo * 100;

  return Math.min(metaAtingida, 100).toFixed(1);
};

export const applyNotaMask = (value: string): string => {
  let cleaned = value.replace(/[^\d.,]/g, "");
  cleaned = cleaned.replace(",", ".");

  if (cleaned.length > 1 && !cleaned.includes(".") && cleaned !== "10") {
    cleaned = cleaned.charAt(0) + "." + cleaned.charAt(1);
  }

  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts[1];
  }

  if (parts.length === 2 && parts[1].length > 1) {
    cleaned = parts[0] + "." + parts[1].charAt(0);
  }

  const numValue = parseFloat(cleaned);
  if (numValue > 10) {
    cleaned = "10.0";
  }

  if (parts[0] && parts[0].length > 2) {
    cleaned = "10.0";
  }

  return cleaned;
};

export const applyTimeMask = (value: string): string => {
  const rawValue = value.replace(/\D/g, "");
  let maskedValue = "";
  for (let i = 0; i < rawValue.length && i < 6; i++) {
    if (i === 2 || i === 4) {
      maskedValue += ":";
    }
    maskedValue += rawValue[i];
  }
  return maskedValue;
};
